import { useRef, useEffect, useCallback } from 'react'
import { drawPanorama, drawStamp } from '../utils/drawUtils'

function useAnimation({ config }) {
  const animationFrameRef = useRef(null)
  const offsetsRef = useRef({ background: 0, foreground: 0 })
  const canvasRefsRef = useRef({ backgroundCanvas: null, foregroundCanvas: null, stampCanvas: null })
  const configRef = useRef(config)

  useEffect(() => {
    configRef.current = config
  }, [config])

  const setCanvasRefs = useCallback((refs) => {
    canvasRefsRef.current = refs
  }, [])

  const animate = useCallback(() => {
    const { backgroundCanvas, foregroundCanvas, stampCanvas } = canvasRefsRef.current
    const cfg = configRef.current

    if (!backgroundCanvas || !foregroundCanvas) {
      animationFrameRef.current = requestAnimationFrame(animate)
      return
    }

    const bgCtx = backgroundCanvas.getContext('2d')
    const fgCtx = foregroundCanvas.getContext('2d')
    const stampCtx = stampCanvas?.getContext('2d')

    if (!bgCtx || !fgCtx) {
      animationFrameRef.current = requestAnimationFrame(animate)
      return
    }

    const cw = backgroundCanvas.width
    const ch = backgroundCanvas.height

    bgCtx.clearRect(0, 0, cw, ch)
    fgCtx.clearRect(0, 0, cw, ch)
    if (stampCtx) stampCtx.clearRect(0, 0, cw, ch)

    // Update offsets
    if (cfg.paused) {
      offsetsRef.current.background = cfg.background.offset
      offsetsRef.current.foreground = cfg.foreground.offset
    } else {
      offsetsRef.current.background += cfg.background.speed
      offsetsRef.current.foreground += cfg.foreground.speed
    }

    // Draw panoramas (with optional motion blur)
    if (cfg.background.image) {
      drawPanorama(
        bgCtx, cfg.background.image,
        offsetsRef.current.background,
        cfg.background.direction,
        cw, ch,
        cfg.background.motionBlur || 0
      )
    }

    if (cfg.foreground.image) {
      drawPanorama(
        fgCtx, cfg.foreground.image,
        offsetsRef.current.foreground,
        cfg.foreground.direction,
        cw, ch,
        cfg.foreground.motionBlur || 0
      )
    }

    // Draw stamps
    if (stampCtx && cfg.stamps?.length) {
      cfg.stamps.forEach(stamp => drawStamp(stampCtx, stamp, cw, ch))
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [])

  const startAnimation = useCallback(() => {
    if (!animationFrameRef.current) animate()
  }, [animate])

  const stopAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [])

  return { startAnimation, stopAnimation, setCanvasRefs }
}

export default useAnimation
